import { FC } from "react"
import { Link as RouterLink } from "react-router-dom"
import { Alert, Box, Button, Link, Stack, Typography } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { DataGrid } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { UserbotTracking as T } from "../../interfaces/tracking"
import { useScheduleRunsQuery } from "../../store"
import { launchResultPath } from "../common/launchResult"

const runColumns: GridColDef<T.ScheduleRun>[] = [
  {
    field: "id",
    headerName: "Запуск",
    flex: 2,
    renderCell: ({ row }) => <span id={`run-${row.id}`}>{row.id}</span>,
  },
  { field: "status", headerName: "Статус", flex: 1 },
  {
    field: "reason",
    headerName: "Причина",
    flex: 1,
    valueGetter: ({ value }) => value ?? "—",
  },
  { field: "total", headerName: "Всего", flex: 1 },
  ...["createdAt", "updatedAt", "finishedAt"].map((field, index) => ({
    field,
    headerName: ["Создан", "Обновлён", "Завершён"][index],
    flex: 2,
    valueGetter: ({ value }: { value: string | null }) =>
      value ? new Date(value).toLocaleString("ru") : "—",
  })),
  {
    field: "result",
    headerName: "Результат",
    flex: 2,
    renderCell: ({ row }) => {
      const result = row.groupId
        ? {
            kind: "group" as const,
            id: row.groupId,
            total: row.total,
            items: [],
          }
        : row.chainId
          ? { id: row.chainId, name: "", stepsCount: 0 }
          : { kind: "no-work" as const, total: 0 as const }
      const path = launchResultPath(result)
      return path ? (
        <Link component={RouterLink} to={path}>
          {row.groupId ? "Группа" : "Отчёт цепочки"}
        </Link>
      ) : (
        "—"
      )
    },
  },
]
const columns = runColumns.map((column) => ({
  ...column,
  sortable: false,
  filterable: false,
}))

export const ScheduledRuns: FC<{ scheduleId: string }> = ({ scheduleId }) => {
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const {
    currentData: data,
    isFetching,
    isError,
    error,
    refetch,
  } = useScheduleRunsQuery(
    { id: scheduleId, limit: pageSize, offset: page * pageSize },
    { pollingInterval: 15000 },
  )
  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Расписание: {scheduleId}. Ожидание, задержка, парковка и отмена не
        означают завершения: нужен сохранённый терминальный отчёт.
      </Typography>
      <Button onClick={() => refetch()}>Обновить</Button>
      {isError && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
      <Box height="70vh">
        <DataGrid<T.ScheduleRun>
          columns={columns}
          rows={data?.items ?? []}
          rowCount={data?.total ?? 0}
          loading={isFetching && !data}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[50, 100, 500]}
          disableColumnFilter
        />
      </Box>
    </Stack>
  )
}
